import { createApi } from '@wellread/api';
import { QueryClient } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { createStore, del, get, set } from 'idb-keyval';
import { config } from '../config';
import { session } from './auth';

// Same origin: the session cookies go with every request by themselves.
export const api = createApi(config.apiUrl, session);
export const { client, call } = api;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 7 * 24 * 60 * 60 * 1000,
      retry: (count, err) =>
        count < 2 && !(err instanceof Error && 'status' in err && Number(err.status) < 500),
      refetchOnWindowFocus: true,
    },
  },
});

// The query cache is persisted to IndexedDB so every screen renders from
// local data first and syncs in the background.
const store = createStore('wellread', 'query-cache');
export const persister = createAsyncStoragePersister({
  storage: {
    getItem: (k) => get<string>(k, store).then((v) => v ?? null),
    setItem: (k, v) => set(k, v, store),
    removeItem: (k) => del(k, store),
  },
  throttleTime: 1000,
});

/** Queries that aren't worth persisting (large, per-session or immutable elsewhere). */
export const volatileKeys = new Set([
  'chapter',
  'printed',
  'page',
  'queue',
  'review',
  'graph',
  'search',
  'booksFor',
]);

export const keys = {
  me: ['me'] as const,
  books: ['books'] as const,
  book: (id: string) => ['book', id] as const,
  contents: (id: string) => ['contents', id] as const,
  chapter: (id: string, idx: number) => ['chapter', id, idx] as const,
  /** Where a PDF book's chapter is printed, and a printed page drawn: the original view. */
  printed: (id: string, idx: number) => ['printed', id, idx] as const,
  page: (id: string, n: number) => ['page', id, n] as const,
  marks: (id: string, idx: number) => ['marks', id, idx] as const,
  annotations: (id: string) => ['annotations', id] as const,
  cast: (id: string) => ['cast', id] as const,
  story: (id: string) => ['story', id] as const,
  tracks: ['tracks'] as const,
  queue: (book?: string, focus?: string, track?: string, cluster?: string, books?: string) =>
    ['queue', book ?? null, focus ?? null, track ?? null, cluster ?? null, books ?? null] as const,
  review: (id: string) => ['review', id] as const,
  /** The reader's vote on a question, for the card before it is answered (a marked one carries its own). */
  vote: (questionId: string) => ['vote', questionId] as const,
  stats: ['stats'] as const,
  node: (id: string) => ['node', id] as const,
  dossier: (book: string, node: string) => ['dossier', book, node] as const,
  graph: (book: string, node: string | undefined, depth: number) =>
    ['graph', book, node ?? null, depth] as const,
  credits: ['credits'] as const,
  /** Where the reader stands and what a month costs. */
  billing: ['billing'] as const,
  explore: ['explore'] as const,
  /** Explore's recent conversations, starters and questions left today; pages of older ones follow. */
  conversations: ['conversations'] as const,
  conversation: (id: string) => ['conversation', id] as const,
  /** What Explore remembers about the reader, and the authors they follow. */
  remembered: ['remembered'] as const,
  /** Books asked for in a line: a track's (useBooksFor). Kept for the visit, never stored. */
  booksFor: (line: string) => ['booksFor', line] as const,
  map: (scope: string, book?: string, cluster?: string, node?: string, track?: string, books?: string) =>
    ['map', scope, book ?? null, cluster ?? null, node ?? null, track ?? null, books ?? null] as const,
};
